import React from 'react';
import { Row, Col, Divider, Tooltip } from 'antd';
import {useTranslation} from 'react-i18next'
import classnames from 'classnames';
import TableContent from '../table-content';
import { formatDate } from 'src/utils/helpers';
import OptMatchImg from 'src/assets/img/optmatch.svg'
import './style.scss';

/**
 * NeedItem component - used in fit and match modals.
 * 
 * @param {Object} props - Props for the NeedItem component.
 * @param {Object} props.need - Need item with all necessary attributes.
 * @param {string} [props.className] - Text for additional classNames for styling purposes.
 * @returns {JSX.Element} The NeedItem component.
 * @example
 *  <NeedItem need={needObject} />
 */
function NeedItem({ need, className }) {
    const {
        title,
        requisitionCode,
        technology,
        level,
        createdAt,
        role,
        startProject,
        workType,
        site,
        client,
        requisitionTalentAttractionEmail,
    } = need;

    const { t } = useTranslation();

    const classNames = classnames('need-item', className);

    return (
        <div className={classNames} aria-label={t('needsTitle')} tabIndex={0}>
            <Row aria-label={t('jobInfo')}>
                <Col span={24} aria-describedby='requisitionName' tabIndex={0}>
                    <div className='title'>
                        <div>{title}</div> <div aria-label={`${t('requisitionCode')} ${requisitionCode}`}>(#{requisitionCode})</div>
                    </div>
                    <div className="subtitle">
                        {t('client')}: {client ?? '-'}
                    </div>
                    { need.interviewRequired === 'Y' &&
                        <span className='subtitle--interview-required'>
                            {t('interviewRequired')}
                        </span>
                    }
                    
                    { need.optMatchPercent && 
                        <Tooltip title={t('optMatchTooltip')}>
                            <div className="optmatch">
                                <img src={OptMatchImg} alt="logo" className="optmatch--img" />
                                <span className="optmatch-text--percent text-uppercase">{need.optMatchPercent} {t('fit')}</span>
                                <span className="optmatch-text--date">{t('suggestionsOptMatch')} {need.matchDateSuggestion} (GMT-3)</span>
                            </div>
                        </Tooltip>
                    }
                </Col>
            </Row>
            <Divider className='divider' />
            <div className='info'>
                <Row>
                    <Col span={12}>
                        <div aria-describedby='role' tabIndex={0}>
                            <TableContent
                                mainContent={
                                    <>
                                        <div className='line-breaker'>
                                            <span className='need-item--title'>{t('role')}</span>
                                            <span className='need-item--subtitle'>
                                                {role}
                                            </span>
                                        </div>
                                    </>
                                }
                                subContent={
                                    <>
                                        {t('tech')}: {technology}<br />
                                        {t('level')}: {level}
                                    </>
                                }
                            />
                        </div>
                        
                    </Col>
                    <Col span={12}>
                        <div aria-describedby='responsible' tabIndex={0}>
                            <TableContent
                                mainContent={
                                    <>
                                        <div className='line-breaker'>
                                            <span className='need-item--title'>{t('responsibleTitle')}</span>
                                            <span className='need-item--subtitle'>
                                                {requisitionTalentAttractionEmail.split('@')[0]}
                                            </span>
                                        </div>
                                    </>
                                }
                                subContent={`${t('createdThisNeed')} ${formatDate(createdAt)}`}
                            />
                        </div>
                        
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <div aria-describedby='location' tabIndex={0}>
                            <TableContent
                                mainContent={
                                    <>
                                        <div className='line-breaker'>
                                            <span className='need-item--title'>{t('location')}</span>
                                            <span className='need-item--subtitle'>
                                                {site}
                                            </span>
                                        </div>
                                    </>
                                }
                                subContent={`${t('typeOfWork')}: ${workType}`}
                            />
                        </div>
                        
                    </Col>
                    <Col span={12}>
                        <div aria-describedby='openingDate' tabIndex={0}>
                            <TableContent
                                mainContent={
                                    <>
                                        <div className='line-breaker'>
                                            <span className='need-item--title'>{t("startProjectDate")}</span>
                                            <span className='need-item--subtitle'>
                                                {formatDate(startProject)}
                                            </span>
                                        </div>
                                    </>
                                }
                            />
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
}

NeedItem.defaultProps = {
    need: {},
    className: '',
};

export default NeedItem;
